import { LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM, LASHING_BRIDGE_MIN_GAP_IN_MM } from "../consts";

import type { BayLcgVcgTcgAndPairings } from "../BayLcgVcgTcgAndPairings/BayLcgVcgTcgAndPairings";
import { BayLevelEnum } from "open-vessel-definition";
import { IBayPattern } from "../types/IPositionPatterns";
import { TLashingBridgeSide } from "./lashingBridgesHelpers";
import { preCalculateCurrentVesselParts } from "../preCalculateCurrentVesselParts";

/** A place where a lashing bridge can stand, and the OVD position (bay and side) that describes it */
export interface ILashingBridgeSlot {
  /** Bay where the bridge is stored: the fwd bay of the block for FWD, the aft bay for AFT */
  isoBay: IBayPattern;
  side: TLashingBridgeSide;
  /** The block of bays: `"005-007"` for a pair, `"009"` for a single bay */
  block: string;
  /** End of the block on that side, in mm (20' and 40' slots only) */
  end?: number;
  /** The neighbour block on that side, labelled like `block`. Undefined at the ends of the ship */
  neighbourBlock?: string;
  /** Opposite end of the neighbour block on that side, in mm. Undefined at the ends of the ship */
  neighbourEnd?: number;
  /** Gap with the neighbour block on that side, in mm. Undefined at the ends of the ship or without LCGs */
  gap?: number;
  /** A vessel part (deckhouse, crane, spacer...) sits between this block and the neighbour block */
  partBetween: boolean;
  /**
   * The position, not offered, that describes the same space: the AFT of the bow block when the
   * blocks are too close for two bridges. Only on FWD slots
   */
  sharedWith?: { isoBay: IBayPattern; side: TLashingBridgeSide };
}

const slotsCache = new WeakMap<object, ILashingBridgeSlot[]>();

/**
 * Places where a lashing bridge can stand, from bow to stern. A block of bays is a 40' pair or
 * a single bay. Between two blocks, the bridge is written as FWD of the stern block's fwd bay:
 * - every block offers its FWD;
 * - a block offers its AFT only when two bridges fit before the next block (a vessel part
 *   follows it, the gap is at least `minDoubleGapInMm`, or the gap is unknown) and on the
 *   stern-most block;
 * - never between the two 20' bays of a pair.
 * Gaps use the real or the artificial LCGs (20' and 40' slots only, as longer containers are
 * stowed above the bridges).
 */
export function getLashingBridgeSlots(
  p: Pick<BayLcgVcgTcgAndPairings, "lcgsBy20Bay" | "bayLevelPositionsDict" | "vesselPartsData">,
  { minDoubleGapInMm = LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM }: { minDoubleGapInMm?: number } = {},
): ILashingBridgeSlot[] {
  const useCache = minDoubleGapInMm === LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM;
  const cached = useCache ? slotsCache.get(p) : undefined;
  if (cached) return cached;

  const bays = Object.values(p.bayLevelPositionsDict)
    .map((blp) => blp.isoBay)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort();

  const blocks = getBlocks(p, bays);
  const sequence = preCalculateCurrentVesselParts(
    bays.map((isoBay) => ({ isoBay })),
    p.vesselPartsData ?? [],
  );

  /** A vessel part sits between the last bay of `a` and the first bay of `b` in the sequence */
  const partBetween = (a: IBayPattern[], b: IBayPattern[]): boolean => {
    const from = sequence.indexOf(a[a.length - 1]);
    const to = sequence.indexOf(b[0]);
    return (
      from >= 0 && to > from && sequence.slice(from + 1, to).some((s) => typeof s !== "string")
    );
  };

  const slots: ILashingBridgeSlot[] = [];

  blocks.forEach((block, i) => {
    const label = block.join("-");
    const ends = getBlockEnds(p, block);
    const prev = blocks[i - 1];
    const next = blocks[i + 1];

    // FWD of the fwd bay
    const prevEnds = prev ? getBlockEnds(p, prev) : undefined;
    const prevPart = prev ? partBetween(prev, block) : false;
    const prevGap = ends && prevEnds ? prevEnds.aft - ends.fore : undefined;
    const prevOffersAft = prev ? offersAft(prevPart, prevGap, minDoubleGapInMm) : true;

    slots.push({
      isoBay: block[0],
      side: "fore",
      block: label,
      end: ends?.fore,
      neighbourBlock: prev?.join("-"),
      neighbourEnd: prevEnds?.aft,
      gap: prevGap,
      partBetween: prevPart,
      sharedWith:
        prev && !prevOffersAft ? { isoBay: prev[prev.length - 1], side: "aft" } : undefined,
    });

    // AFT of the aft bay, only when two bridges fit before the next block (or at the stern)
    const nextEnds = next ? getBlockEnds(p, next) : undefined;
    const nextPart = next ? partBetween(block, next) : false;
    const nextGap = ends && nextEnds ? ends.aft - nextEnds.fore : undefined;

    if (!next || offersAft(nextPart, nextGap, minDoubleGapInMm))
      slots.push({
        isoBay: block[block.length - 1],
        side: "aft",
        block: label,
        end: ends?.aft,
        neighbourBlock: next?.join("-"),
        neighbourEnd: nextEnds?.fore,
        gap: nextGap,
        partBetween: nextPart,
      });
  });

  if (useCache) slotsCache.set(p, slots);
  return slots;
}

function offersAft(partBetween: boolean, gap: number | undefined, minDoubleGapInMm: number) {
  return partBetween || gap === undefined || gap >= minDoubleGapInMm;
}

/**
 * Median gap between neighbour blocks with no vessel part in between, at least
 * {@link LASHING_BRIDGE_MIN_GAP_IN_MM}: where bridges usually stand
 */
export function getTypicalLashingBridgeGap(slots: ILashingBridgeSlot[]): number | undefined {
  const gaps = slots
    .filter((s) => s.side === "fore" && !s.partBetween && s.gap !== undefined)
    .map((s) => s.gap as number)
    .filter((g) => g >= LASHING_BRIDGE_MIN_GAP_IN_MM)
    .sort((a, b) => a - b);

  if (!gaps.length) return undefined;
  const mid = gaps.length / 2;
  return gaps.length % 2 ? gaps[Math.floor(mid)] : (gaps[mid - 1] + gaps[mid]) * 0.5;
}

/** Blocks of bays, bow to stern: a bay and its confirmed paired bay, or a single bay */
function getBlocks(
  p: Pick<BayLcgVcgTcgAndPairings, "lcgsBy20Bay">,
  bays: IBayPattern[],
): IBayPattern[][] {
  const assigned = new Set<IBayPattern>();
  const blocks: IBayPattern[][] = [];

  bays.forEach((bay) => {
    if (assigned.has(bay)) return;
    const paired = p.lcgsBy20Bay[bay]?.confirmedPairedBay;
    const block =
      paired && bays.includes(paired) && !assigned.has(paired)
        ? ([bay, paired].sort() as IBayPattern[])
        : [bay];
    block.forEach((b) => assigned.add(b));
    blocks.push(block);
  });

  return blocks.sort((a, b) => Number(a[0]) - Number(b[0]));
}

/** Fore and aft ends of a block of bays */
function getBlockEnds(
  p: Pick<BayLcgVcgTcgAndPairings, "lcgsBy20Bay">,
  block: IBayPattern[],
): { fore: number; aft: number } | undefined {
  const ends = block.map((b) => getBayEnds(p, b)).filter((e) => e !== undefined);
  if (!ends.length) return undefined;
  return {
    fore: Math.max(...ends.map((e) => e.fore)),
    aft: Math.min(...ends.map((e) => e.aft)),
  };
}

/** Fore and aft ends of a 20' bay from its 20' and 40' slots (or all its slots if it has none of those) */
export function getBayEnds(
  p: Pick<BayLcgVcgTcgAndPairings, "lcgsBy20Bay">,
  isoBay: IBayPattern,
): { fore: number; aft: number } | undefined {
  const lcgs20 = p.lcgsBy20Bay[isoBay];
  if (!lcgs20) return undefined;

  let fore = -Infinity;
  let aft = Infinity;
  lcgs20.lcgs.forEach((triplet, size) => {
    if (size > 40) return;
    if (triplet.foreLcg > fore) fore = triplet.foreLcg;
    if (triplet.aftLcg < aft) aft = triplet.aftLcg;
  });

  if (!Number.isFinite(fore) || !Number.isFinite(aft)) {
    fore = lcgs20.maxForeLcg;
    aft = lcgs20.minAftLcg;
  }

  return Number.isFinite(fore) && Number.isFinite(aft) ? { fore, aft } : undefined;
}

/**
 * Fore and aft ends of the containers longer than 40' that some bays stow low, with their bottom
 * less than `heightInMm` over the stack bottom of their row (above deck). They overhang the
 * 20' and 40' ends, so a lashing bridge lower than that must clear them.
 * @returns `undefined` when the bays stow no longer containers that low
 */
export function getLowLongContainerEnds(
  p: Pick<BayLcgVcgTcgAndPairings, "lcgsBy20Bay" | "bayLevelPositionsDict">,
  bays: IBayPattern[],
  heightInMm: number,
  tierHeightInMm: number,
): { fore: number; aft: number } | undefined {
  let fore = -Infinity;
  let aft = Infinity;

  bays.forEach((isoBay) => {
    const blp = p.bayLevelPositionsDict[`${isoBay}-${BayLevelEnum.ABOVE}`];
    const lcgs = p.lcgsBy20Bay[isoBay]?.lcgs;
    if (!blp || !lcgs) return;

    blp.cellsInfo.forEach((cell) => {
      if (cell.restricted) return;
      const bottomTier = Number(blp.rowBottoms.get(cell.row)?.bottomTier ?? blp.minTier);
      const bottom = ((Number(cell.tier) - bottomTier) / 2) * tierHeightInMm;
      if (!Number.isFinite(bottom) || bottom >= heightInMm) return;

      const sizes = cell.sizes ? [...cell.sizes.keys()] : cell.maxSize ? [cell.maxSize] : [];
      sizes.forEach((size) => {
        const triplet = size > 40 ? lcgs.get(size) : undefined;
        if (!triplet) return;
        if (triplet.foreLcg > fore) fore = triplet.foreLcg;
        if (triplet.aftLcg < aft) aft = triplet.aftLcg;
      });
    });
  });

  return Number.isFinite(fore) && Number.isFinite(aft) ? { fore, aft } : undefined;
}
